import React, { useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Loader2 } from 'lucide-react';

export default function LoginForm() {
  const { login } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await login(email, password);
      // Redirect will be handled by the auth context and route protection
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen p-2">
      <div className="min-h-[calc(100vh-1rem)] grid grid-cols-1 lg:grid-cols-2 rounded-lg overflow-hidden shadow-2xl">
      {/* Left Side - Image Background */}
      <div 
        className="hidden lg:flex items-center justify-center relative overflow-hidden bg-gradient-to-br from-gray-900 to-gray-700"
      >
        {/* Background Image Layer */}
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{ 
            backgroundImage: 'url(/assets/healthcare-bw.jpg)',
            opacity: 0.15
          }}
        />
        
        {/* Gradient Overlay */}
        <div 
          className="absolute inset-0 bg-gradient-to-br from-gray-900/80 to-gray-700/80"
        />
        
        {/* Content Container */}
        <div className="relative z-10 text-center px-8" style={{ maxWidth: '32rem' }}>
          <h2 className="font-bold text-white mb-4" style={{ fontSize: '2.25rem' }}>
            VBCA MIPS Platform
          </h2>
          <p style={{ fontSize: '1.125rem', color: 'hsl(220, 9%, 79%)' }}>
            Advanced analytics and expert guidance for value-based care excellence
          </p>
        </div>
      </div>

      {/* Right Side - Login Form */}
      <div 
        className="flex items-center justify-center p-8"
        style={{ backgroundColor: 'hsl(210, 30%, 98%)' }}
      >
        <div className="w-full max-w-[28rem]" style={{ animation: 'fadeIn 0.3s ease-out' }}>
          <div 
            className="relative overflow-hidden rounded-2xl p-8"
            style={{ 
              backgroundColor: 'hsl(0, 0%, 100%)',
              boxShadow: '0 20px 50px rgba(0, 0, 0, 0.3)',
              border: '1px solid hsl(214.3, 31.8%, 91.4%)'
            }}
          >
            {/* Black Accent Bar */}
            <div 
              className="absolute left-0 top-0 bottom-0"
              style={{ 
                width: '4px',
                backgroundColor: 'hsl(222.2, 84%, 4.9%)' 
              }}
            />
            
            {/* Header */}
            <div className="text-center mb-6">
              <h1 
                className="text-2xl font-bold mb-3"
                style={{ color: 'hsl(222.2, 84%, 4.9%)' }}
              >
                Sign In
              </h1>
              <p 
                className="text-sm mb-3"
                style={{ color: 'hsl(215.4, 16.3%, 46.9%)' }}
              >
                Empowering Practices in Value-Based Care
              </p>
              <p 
                className="text-xs"
                style={{ color: 'hsl(215.4, 16.3%, 46.9%)' }}
              >
                Enter your credentials to access the VBCA MIPS application
              </p>
            </div>

            {/* Form */}
            <form onSubmit={handleSubmit} className="mb-6">
              {error && (
                <Alert variant="destructive" className="mb-4">
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}
              
              <div className="mb-4">
                <label 
                  htmlFor="email" 
                  className="block text-sm font-medium mb-2"
                  style={{ color: 'hsl(222.2, 84%, 4.9%)' }}
                >
                  Email
                </label>
                <input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  disabled={loading}
                  placeholder="Enter your email"
                  className="w-full h-10 px-3 text-sm rounded-lg transition-all"
                  style={{
                    border: '1px solid hsl(214.3, 31.8%, 91.4%)',
                    backgroundColor: 'hsl(0, 0%, 100%)',
                    color: 'hsl(222.2, 84%, 4.9%)',
                    outline: 'none'
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = 'hsl(222.2, 84%, 4.9%)';
                    e.target.style.boxShadow = '0 0 0 2px hsla(222.2, 84%, 4.9%, 0.1)';
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = 'hsl(214.3, 31.8%, 91.4%)';
                    e.target.style.boxShadow = 'none';
                  }}
                />
              </div>
              
              <div className="mb-4">
                <label 
                  htmlFor="password" 
                  className="block text-sm font-medium mb-2"
                  style={{ color: 'hsl(222.2, 84%, 4.9%)' }}
                >
                  Password
                </label>
                <input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  disabled={loading}
                  placeholder="Enter your password"
                  className="w-full h-10 px-3 text-sm rounded-lg transition-all"
                  style={{
                    border: '1px solid hsl(214.3, 31.8%, 91.4%)',
                    backgroundColor: 'hsl(0, 0%, 100%)',
                    color: 'hsl(222.2, 84%, 4.9%)',
                    outline: 'none'
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = 'hsl(222.2, 84%, 4.9%)';
                    e.target.style.boxShadow = '0 0 0 2px hsla(222.2, 84%, 4.9%, 0.1)';
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = 'hsl(214.3, 31.8%, 91.4%)';
                    e.target.style.boxShadow = 'none';
                  }}
                />
              </div>
              
              <button
                type="submit"
                disabled={loading}
                className="w-full h-11 rounded-lg font-medium text-sm transition-all flex items-center justify-center"
                style={{
                  backgroundColor: 'hsl(222.2, 84%, 4.9%)',
                  color: 'hsl(210, 40%, 98%)',
                  border: 'none',
                  cursor: loading ? 'not-allowed' : 'pointer',
                  opacity: loading ? 0.5 : 1
                }}
                onMouseEnter={(e) => {
                  if (!loading) {
                    e.currentTarget.style.backgroundColor = 'hsla(222.2, 84%, 4.9%, 0.9)';
                    e.currentTarget.style.transform = 'scale(1.02)';
                    e.currentTarget.style.boxShadow = '0 4px 6px rgba(0, 0, 0, 0.1)';
                  }
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'hsl(222.2, 84%, 4.9%)';
                  e.currentTarget.style.transform = 'scale(1)';
                  e.currentTarget.style.boxShadow = 'none';
                }}
                onMouseDown={(e) => {
                  if (!loading) {
                    e.currentTarget.style.transform = 'scale(0.98)';
                  }
                }}
                onMouseUp={(e) => {
                  if (!loading) {
                    e.currentTarget.style.transform = 'scale(1.02)';
                  }
                }}
              >
                {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Sign In
              </button>
            </form>

            {/* Account recovery is handled by an administrator (no self-service signup). */}
            <div className="text-center text-sm mb-6" style={{ color: 'hsl(215.4, 16.3%, 46.9%)' }}>
              Forgot your password? Contact your administrator to have it reset.
            </div>

            {/* Footer */}
            <div 
              className="text-center pt-4"
              style={{ borderTop: '1px solid hsl(214.3, 31.8%, 91.4%)' }}
            >
              <p 
                className="text-xs"
                style={{ color: 'hsl(215.4, 16.3%, 46.9%)' }}
              >
                © 2025 Value-Based Care Advisors. All rights reserved.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>

      {/* Add keyframes for fadeIn animation */}
      <style jsx>{`
        @keyframes fadeIn {
          0% {
            opacity: 0;
            transform: translateY(10px);
          }
          100% {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>
    </div>
  );
}